// src/pages/menu-items/LoungeMenuItemList.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Divider,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  RestaurantMenu as MenuIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  FilterList as FilterIcon,
  Store as StoreIcon,
  History as HistoryIcon,
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';

// Reuse the EXACT same layout components as LoungeTables
const LoungeSidebar = lazy(() => import('../../../../components/RestaurantPOS/manager/lounge/Sidebar_consultants'));
const Header = lazy(() => import('../../../../components/RestaurantPOS/manager/Header_Consultants'));
const Footer = lazy(() => import('../../../../components/RestaurantPOS/manager/Footer'));
const Preloader = lazy(() => import('../../../../theme/Preloader'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// Lounge-only config (must match LoungeTables)
const LOUNGE_CONFIG = {
  key: 'lounge',
  id: 4,
  name: 'Lounge',
  color: '#6A1B9A',
  lightColor: '#9C27B0',
  icon: '🍸',
};

// ============================================
// TOKEN UTILITIES
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

// ============================================
// SUB-COMPONENTS
// ============================================

const StatsCard = React.memo(({ stats, locationColor }) => {
  const statItems = [
    { label: 'Total Items', value: stats.total, color: locationColor, icon: <MenuIcon sx={{ fontSize: 20 }} /> },
    { label: 'Active', value: stats.active, color: '#4caf50', icon: <CheckCircleIcon sx={{ fontSize: 20 }} /> },
    { label: 'Inactive', value: stats.inactive, color: '#f44336', icon: <CancelIcon sx={{ fontSize: 20 }} /> },
    { label: 'Food Items', value: stats.food || 0, color: '#FF6F00', icon: <FoodIcon sx={{ fontSize: 20 }} /> },
    { label: 'Drinks', value: stats.drinks || 0, color: '#00695C', icon: <DrinkIcon sx={{ fontSize: 20 }} /> },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((item) => (
        <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.label}>
          <Card
            sx={{
              borderRadius: 2,
              borderLeft: `4px solid ${item.color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                  {item.label}
                </Typography>
                {item.icon}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: item.color }}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

// Read-only menu item row — only View & View Stock
const MenuItemRow = React.memo(({ item, onView, onViewStock, index }) => {
  const theme = useTheme();
  const statusColor = item.is_active ? '#4caf50' : '#f44336';
  const statusLabel = item.is_active ? 'Active' : 'Inactive';
  const mainCategoryColor = item.main_category === 'Food' ? '#FF6F00' : '#00695C';

  return (
    <TableRow
      sx={{
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {item.category}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.main_category}
          size="small"
          icon={item.main_category === 'Food' ? <FoodIcon sx={{ fontSize: 14 }} /> : <DrinkIcon sx={{ fontSize: 14 }} />}
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(mainCategoryColor, 0.1),
            color: mainCategoryColor,
            fontSize: '0.75rem',
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={700} color="primary">
          ₦{item.amount?.toFixed(2) || '0.00'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.85rem',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.description || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => onView(item)} sx={{ color: theme.palette.info.main }}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Stock">
            <IconButton size="small" onClick={() => onViewStock(item)} sx={{ color: theme.palette.info.main }}>
              <StoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// ============================================
// MAIN COMPONENT — Read-only, Lounge only
// ============================================

const LoungeMenuItemList = () => {
  const theme = useTheme();
  const locationConfig = useMemo(() => LOUNGE_CONFIG, []);
  const { name: locationName, id: locationIdValue, color: locationColor } = locationConfig;

  // State
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Menu');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, food: 0, drinks: 0 });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  const [mainCategoryFilter, setMainCategoryFilter] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);

  // View-only dialog
  const [viewDialog, setViewDialog] = useState({ open: false, item: null });

  // View Stock dialog
  const [viewStockDialogOpen, setViewStockDialogOpen] = useState(false);
  const [viewStockItem, setViewStockItem] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [stockMovements, setStockMovements] = useState([]);
  const [loadingStockData, setLoadingStockData] = useState(false);

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  const getAuthToken = useCallback(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || isTokenExpired(token)) return null;
      return token;
    } catch (err) {
      console.error('[LoungeMenuItemList] Error getting auth token:', err);
      return null;
    }
  }, []);

  const refreshAuthToken = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token available');

      const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) throw new Error('Refresh failed');

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        return data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[LoungeMenuItemList] Token refresh failed:', err);
      localStorage.clear();
      window.location.href = '/restaurantAuth';
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    let token = getAuthToken();
    if (!token) {
      token = await refreshAuthToken();
      if (!token) throw new Error('Authentication required');
    }

    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const url = `${FULL_API_URL}${endpoint}`;

    try {
      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        const newToken = await refreshAuthToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, { ...options, headers });
          if (retryResponse.ok) {
            const ct = retryResponse.headers.get('content-type');
            if (ct && ct.includes('application/json')) return await retryResponse.json();
            return { message: await retryResponse.text() };
          }
          if (retryResponse.status === 401) throw new Error('Authentication failed after refresh');
        }
        throw new Error('Authentication failed');
      }

      const ct = response.headers.get('content-type');
      let data;
      if (ct && ct.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || 'Non-JSON response received' };
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (err) {
      console.error('[LoungeMenuItemList] API Error:', err);
      throw err;
    }
  }, [getAuthToken, refreshAuthToken]);

  // ============================================
  // FETCH
  // ============================================

  const fetchMenuItems = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = `/menu-items/?location_id=${locationIdValue}&limit=1000`;
      const data = await apiRequest(endpoint);
      const itemsData = Array.isArray(data) ? data : [];

      setItems(itemsData);
      setFilteredItems(itemsData);

      const active = itemsData.filter(i => i.is_active).length;
      const food = itemsData.filter(i => i.main_category === 'Food').length;
      const drinks = itemsData.filter(i => i.main_category === 'Drinks').length;

      setStats({
        total: itemsData.length,
        active,
        inactive: itemsData.length - active,
        food,
        drinks,
      });
    } catch (err) {
      console.error('[LoungeMenuItemList] Error fetching menu items:', err);
      if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationIdValue]);

  const fetchInventoryForMenuItem = useCallback(async (menuItemId) => {
    try {
      const endpoint = `/inventory/items?menu_item_id=${menuItemId}&location_id=${locationIdValue}`;
      const data = await apiRequest(endpoint);
      return Array.isArray(data) ? data[0] : null;
    } catch (err) {
      console.error('[LoungeMenuItemList] Error fetching inventory:', err);
      return null;
    }
  }, [apiRequest, locationIdValue]);

  const fetchStockMovements = useCallback(async (inventoryItemId) => {
    try {
      const endpoint = `/inventory/movements?inventory_item_id=${inventoryItemId}&limit=500`;
      const data = await apiRequest(endpoint);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('[LoungeMenuItemList] Error fetching stock movements:', err);
      return [];
    }
  }, [apiRequest]);

  // ============================================
  // VIEW HANDLERS
  // ============================================

  const handleOpenView = useCallback((item) => {
    setViewDialog({ open: true, item });
  }, []);

  const handleCloseView = useCallback(() => {
    setViewDialog({ open: false, item: null });
  }, []);

  const handleViewStock = useCallback(async (item) => {
    setViewStockItem(item);
    setViewStockDialogOpen(true);
    setLoadingStockData(true);

    try {
      const inventory = await fetchInventoryForMenuItem(item.id);
      setInventoryData(inventory);

      if (inventory) {
        const movements = await fetchStockMovements(inventory.id);
        setStockMovements(movements);
      } else {
        setStockMovements([]);
      }
    } catch (err) {
      console.error('[LoungeMenuItemList] Error fetching stock data:', err);
    } finally {
      setLoadingStockData(false);
    }
  }, [fetchInventoryForMenuItem, fetchStockMovements]);

  const handleCloseViewStockDialog = useCallback(() => {
    setViewStockDialogOpen(false);
    setViewStockItem(null);
    setInventoryData(null);
    setStockMovements([]);
    setLoadingStockData(false);
  }, []);

  // ============================================
  // SEARCH / FILTER / SORT
  // ============================================

  const applyFilters = useCallback(() => {
    let filtered = [...items];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
      );
    }

    if (mainCategoryFilter !== 'all') {
      filtered = filtered.filter(item => item.main_category === mainCategoryFilter);
    }

    if (isPriceFilterActive) {
      filtered = filtered.filter(item => item.amount >= priceRange[0] && item.amount <= priceRange[1]);
    }

    setFilteredItems(filtered);
    setPage(0);
  }, [items, searchTerm, mainCategoryFilter, priceRange, isPriceFilterActive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearch = useCallback((event) => setSearchTerm(event.target.value), []);
  const handleClearSearch = useCallback(() => setSearchTerm(''), []);

  const handleMainCategoryFilterChange = useCallback((event, newValue) => {
    if (newValue !== null) setMainCategoryFilter(newValue);
  }, []);

  const handlePriceRangeChange = useCallback((event, newValue) => {
    setPriceRange(newValue);
    setIsPriceFilterActive(true);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setMainCategoryFilter('all');
    setPriceRange([0, 100000]);
    setIsPriceFilterActive(false);
    setPage(0);
  }, []);

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    const sorted = [...filteredItems].sort((a, b) => {
      if (property === 'name') return isAsc ? a.name?.localeCompare(b.name) : b.name?.localeCompare(a.name);
      if (property === 'amount') return isAsc ? (a.amount || 0) - (b.amount || 0) : (b.amount || 0) - (a.amount || 0);
      if (property === 'category') return isAsc ? a.category?.localeCompare(b.category) : b.category?.localeCompare(a.category);
      return 0;
    });
    setFilteredItems(sorted);
  }, [orderBy, order, filteredItems]);

  // ============================================
  // SESSION
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const timeLeft = getTokenExpiryTime(token) - Date.now();
      if (timeLeft > 0 && timeLeft < 120000) {
        await refreshAuthToken();
      }
    };
    const interval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(interval);
  }, [refreshAuthToken]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
    fetchMenuItems();
  }, [fetchMenuItems]);

  useEffect(() => {
    fetchMenuItems();
  }, [fetchMenuItems, refreshKey]);

  // ============================================
  // RENDER
  // ============================================

  if (isLoading || isRefreshing) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Menu Items...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <LoungeSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />

            <div className="epos-content">
              {/* Hero Card — no Add button */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 800 }}>
                    {locationConfig.icon} {locationName} Menu Items
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      View food and beverage items with inventory tracking
                    </div>
                  </div>
                </div>
              </div>

              <StatsCard stats={stats} locationColor={locationColor} />

              {/* Search & Filters */}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: '200px' }}>
                    <TextField
                      size="small"
                      placeholder="Search items by name, category, or description..."
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleClearSearch}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flex: 1, maxWidth: '400px' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {filteredItems.length} items found
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Clear All Filters">
                      <IconButton onClick={handleClearAllFilters} color="primary" size="small">
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                      <IconButton onClick={handleRefresh} disabled={loading} size="small">
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Filter by:
                    </Typography>
                  </Box>

                  <ToggleButtonGroup
                    value={mainCategoryFilter}
                    exclusive
                    onChange={handleMainCategoryFilterChange}
                    size="small"
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="Food">
                      <FoodIcon sx={{ fontSize: 16, mr: 0.5 }} /> Food
                    </ToggleButton>
                    <ToggleButton value="Drinks">
                      <DrinkIcon sx={{ fontSize: 16, mr: 0.5 }} /> Drinks
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Price: ₦{priceRange[0]} - ₦{priceRange[1]}
                      {isPriceFilterActive && (
                        <Chip
                          label="Filter Active"
                          size="small"
                          color="primary"
                          sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                    </Typography>
                    <Slider
                      value={priceRange}
                      onChange={handlePriceRangeChange}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100000}
                      size="small"
                      sx={{ width: 200 }}
                    />
                  </Box>
                </Box>
              </Paper>

              {loading && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress color="primary" />
                </Box>
              )}

              <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Item Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'category'}
                            direction={orderBy === 'category' ? order : 'asc'}
                            onClick={() => handleSort('category')}
                          >
                            Category
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Main Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'amount'}
                            direction={orderBy === 'amount' ? order : 'asc'}
                            onClick={() => handleSort('amount')}
                          >
                            Price (₦)
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <MenuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                              No Menu Items Found
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {items.length > 0 ? 'Try adjusting your search or filters' : 'No items have been added yet'}
                            </Typography>
                            {items.length > 0 && (
                              <Button variant="outlined" size="small" onClick={handleClearAllFilters} sx={{ mt: 2 }}>
                                Clear All Filters
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredItems
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((item, index) => (
                            <MenuItemRow
                              key={item.id}
                              item={item}
                              index={page * rowsPerPage + index}
                              onView={handleOpenView}
                              onViewStock={handleViewStock}
                            />
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredItems.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Paper>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ============================================ */}
      {/* VIEW-ONLY MENU ITEM DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={viewDialog.open}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Menu Item Details — {locationName}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          {viewDialog.item && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MenuIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary">Item Name:</Typography>
                  <Typography variant="body1" fontWeight="bold">{viewDialog.item.name}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary">Category:</Typography>
                  <Typography variant="body1">{viewDialog.item.category}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {viewDialog.item.main_category === 'Food'
                    ? <FoodIcon fontSize="small" color="action" />
                    : <DrinkIcon fontSize="small" color="action" />}
                  <Typography variant="body2" color="textSecondary">Main Category:</Typography>
                  <Typography variant="body1">{viewDialog.item.main_category}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary">Price:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    ₦{viewDialog.item.amount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">Status:</Typography>
                  <Chip
                    label={viewDialog.item.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      backgroundColor: alpha(viewDialog.item.is_active ? '#4caf50' : '#f44336', 0.1),
                      color: viewDialog.item.is_active ? '#4caf50' : '#f44336',
                      fontWeight: 700,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary">Created:</Typography>
                  <Typography variant="body1">
                    {viewDialog.item.created_date
                      ? new Date(viewDialog.item.created_date).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              {viewDialog.item.description && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="textSecondary" gutterBottom>Description:</Typography>
                  <Typography variant="body1">{viewDialog.item.description}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseView} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* VIEW STOCK DIALOG (read-only) */}
      {/* ============================================ */}
      <Dialog
        open={viewStockDialogOpen}
        onClose={handleCloseViewStockDialog}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StoreIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              Stock Information: {viewStockItem?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewStockItem?.category} • ₦{viewStockItem?.amount?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseViewStockDialog} size="small">
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loadingStockData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : inventoryData ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#4caf50', 0.04), borderLeft: '4px solid #4caf50' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {inventoryData.quantity} {inventoryData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#ff9800', 0.04), borderLeft: '4px solid #ff9800' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {inventoryData.reorder_point} {inventoryData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#2196f3', 0.04), borderLeft: '4px solid #2196f3' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Chip
                        label={inventoryData.status?.replace('_', ' ') || 'Unknown'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: alpha(
                            inventoryData.status === 'IN_STOCK' ? '#4caf50' :
                            inventoryData.status === 'LOW_STOCK' ? '#ff9800' :
                            inventoryData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                            0.1
                          ),
                          color: inventoryData.status === 'IN_STOCK' ? '#4caf50' :
                                 inventoryData.status === 'LOW_STOCK' ? '#ff9800' :
                                 inventoryData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#9c27b0', 0.04), borderLeft: '4px solid #9c27b0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Value</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        ₦{((inventoryData.quantity || 0) * (inventoryData.cost_per_unit || 0)).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                Stock Movement History
              </Typography>

              {stockMovements.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No stock movements recorded for this item
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Previous</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockMovements.slice(0, 50).map((movement) => {
                        const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
                        return (
                          <TableRow key={movement.id}>
                            <TableCell>{new Date(movement.created_at).toLocaleString()}</TableCell>
                            <TableCell>
                              <Chip
                                label={movement.movement_type}
                                size="small"
                                icon={isIn ? <InIcon fontSize="small" /> : <OutIcon fontSize="small" />}
                                sx={{
                                  backgroundColor: alpha(isIn ? '#4caf50' : '#f44336', 0.1),
                                  color: isIn ? '#4caf50' : '#f44336',
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
                              {isIn ? '+' : '-'}{movement.quantity} {movement.unit}
                            </TableCell>
                            <TableCell>{movement.previous_quantity}</TableCell>
                            <TableCell>{movement.new_quantity}</TableCell>
                            <TableCell>{movement.reason || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <StoreIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">No Inventory Found</Typography>
              <Typography variant="body2" color="textSecondary">
                No inventory item has been created for this menu item yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewStockDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Suspense>
  );
};

export default LoungeMenuItemList;